import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { reviews, animeList, categories } from "@/mocks/animeData";

const formSubmitUrl = "http://yuanyunge.cn/";

const stats = [
  { icon: "ri-movie-2-line", number: "5000+", label: "动漫资源", color: "primary" },
  { icon: "ri-user-3-line", number: "2000万+", label: "注册用户", color: "accent" },
  { icon: "ri-time-line", number: "10年", label: "运营经验", color: "secondary" },
  { icon: "ri-global-line", number: "99%", label: "用户满意度", color: "primary" },
];

const faqItems = [
  {
    question: "樱花动漫网的资源是免费的吗？",
    answer: "是的，樱花动漫网的所有动漫、电视剧和电影资源均免费提供高清在线观看服务，无需注册或付费。我们致力于为广大用户提供最优质的免费观影体验。",
  },
  {
    question: "网站更新频率是怎样的？",
    answer: "樱花动漫网每日同步更新最新番剧，日本动漫一般在播出后24小时内上线，国产动漫和电视剧也会第一时间跟进更新。您可以在追番时间表中查看每日更新计划。",
  },
  {
    question: "支持哪些设备观看？",
    answer: "樱花动漫网全面支持PC端、手机端和平板端访问。网站采用响应式设计，自动适配不同屏幕尺寸，确保在各种设备上都能获得流畅的观看体验。",
  },
  {
    question: "如何联系樱花动漫网？",
    answer: "您可以通过页面底部的联系表单发送邮件，或关注我们的官方微博、B站账号和微信公众号获取最新资讯。我们欢迎用户的反馈和建议。",
  },
];

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [contactStatus, setContactStatus] = useState<"idle" | "success" | "error">("idle");
  const [contactError, setContactError] = useState("");

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError("");

    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = (formData.get("phone_alt") as string || "").trim();

    if (honeypot) {
      setContactStatus("success");
      setContactForm({ name: "", email: "", message: "" });
      return;
    }

    try {
      const res = await fetch(formSubmitUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          name: contactForm.name,
          email: contactForm.email,
          message: contactForm.message,
        }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch {}
      if (res.ok && parsed?.code === "OK") {
        setContactStatus("success");
        setContactForm({ name: "", email: "", message: "" });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
        if (serverMsg.includes("spam")) {
          setContactStatus("success");
        } else {
          setContactStatus("error");
          setContactError(serverMsg || "提交失败，请稍后重试");
        }
      }
    } catch (_err) {
      setContactStatus("error");
      setContactError("网络错误，请检查连接后重试");
    }
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "樱花动漫网",
    url: "http://yuanyunge.cn/",
    description: "樱花动漫网提供免费高清动漫、电视剧、电影在线观看，一站式动漫大全平台。",
    sameAs: ["#", "#", "#"],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "客服支持",
      email: "hello@yuanyunge.cn",
      availableLanguage: "Chinese",
    },
  };

  const totalEpisodes = animeList.reduce(
    (sum, a) => sum + a.episodes.length,
    0
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <SchemaOrg type="Organization" data={orgJsonLd} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
        <SchemaOrg
          type="WebPage"
          data={{
            name: "关于我们 - 樱花动漫网",
            url: "http://yuanyunge.cn/about",
            description:
              "了解樱花动漫网的品牌故事、平台数据、用户口碑及联系方式。免费高清动漫在线观看平台。",
            inLanguage: "zh-CN",
          }}
        />
        <Breadcrumb items={breadcrumbItems} />

        {/* Hero */}
        <section className="relative py-20 md:py-28 bg-background-950 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20cherry%20blossom%20petals%20floating%20in%20a%20starry%20night%20sky%2C%20soft%20pink%20and%20deep%20navy%20blue%20gradient%2C%20minimalist%20abstract%20digital%20art%2C%20ethereal%20and%20dreamy%20atmosphere%2C%20wide%20cinematic%20composition%2C%20simple%20clean%20background&width=1920&height=600&seq=about-hero-01&orientation=landscape"
              alt="樱花动漫网品牌背景"
              title="樱花动漫网品牌背景"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 to-background-950/60"></div>
          <div className="relative z-10 w-full px-4 md:px-8 max-w-5xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full border border-white/30 text-white/70 text-xs font-medium mb-6">
              关于樱花动漫
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white leading-tight">
              让每一次观看
              <br />
              <span className="text-primary-300">都值得期待</span>
            </h1>
            <p className="text-white/60 text-base mt-6 max-w-xl mx-auto leading-relaxed">
              樱花动漫网成立于2016年，致力于为中文用户提供免费、高清、流畅的动漫在线观看体验。
              十年如一日，我们用热爱和专业守护每一位漫迷的追番之旅。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-2xl p-6 md:p-8 text-center"
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 ${
                      stat.color === "primary"
                        ? "bg-primary-50"
                        : stat.color === "accent"
                          ? "bg-accent-50"
                          : "bg-secondary-100"
                    }`}
                  >
                    <i
                      className={`${stat.icon} text-xl ${
                        stat.color === "primary"
                          ? "text-primary-500"
                          : stat.color === "accent"
                            ? "text-accent-500"
                            : "text-secondary-500"
                      }`}
                    ></i>
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-950">
                    {stat.number}
                  </p>
                  <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-secondary-50">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    樱花动漫网诞生于一群热爱动漫的年轻人的初心。2016年，当国内动漫资源分散、画质参差不齐时，我们决定搭建一个<strong>免费、高清、便捷</strong>的一站式动漫观看平台。
                  </p>
                  <p>
                    十年间，我们见证了国漫崛起、日漫风靡、以及无数经典作品的诞生与传承。从最初的几百部作品，发展到如今覆盖<strong>5000+部动漫、电视剧、电影</strong>的庞大资源库，我们始终坚持免费分享的理念。
                  </p>
                  <p>
                    截至目前，平台已累计服务超过<strong>2000万</strong>用户，每日活跃用户超过<strong>50万</strong>，累计播放时长突破<strong>10亿小时</strong>。我们收录的动漫剧集总数已达 <strong>{totalEpisodes}+</strong> 集。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20anime%20watching%20scene%20with%20a%20comfortable%20living%20room%20setup%2C%20soft%20ambient%20lighting%20from%20a%20large%20screen%20TV%2C%20cherry%20blossom%20decorations%2C%20snacks%20and%20drinks%20on%20a%20coffee%20table%2C%20warm%20golden%20tones%2C%20peaceful%20evening%20atmosphere%2C%20Japanese%20aesthetic%20interior%2C%20simple%20clean%20background&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="樱花动漫网品牌故事"
                  title="樱花动漫网品牌故事"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-sm mt-2">
                来自2000万真实用户的声音
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {reviews.map((review, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-2xl p-6 md:p-8"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">
                        {review.name}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {review.role}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {review.content}
                  </p>
                  <div className="flex items-center gap-1 mt-4">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i
                        key={s}
                        className="ri-star-fill text-accent-500 text-xs"
                      ></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 md:py-20 bg-secondary-50">
          <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队将在24小时内回复您的邮件。
                </p>

                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">
                        hello@yuanyunge.cn
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">公司地址</p>
                      <p className="text-sm font-medium text-foreground-900">
                        北京市海淀区中关村科技园
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-secondary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">服务时间</p>
                      <p className="text-sm font-medium text-foreground-900">
                        每日 09:00 - 22:00
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <p className="text-sm font-semibold text-foreground-900 mb-3">
                    社交媒体
                  </p>
                  <div className="flex items-center gap-3">
                    <a
                      href="#"
                      className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors"
                      rel="nofollow"
                    >
                      <i className="ri-weibo-line text-lg"></i>
                    </a>
                    <a
                      href="#"
                      className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-accent-50 hover:text-accent-500 transition-colors"
                      rel="nofollow"
                    >
                      <i className="ri-bilibili-line text-lg"></i>
                    </a>
                    <a
                      href="#"
                      className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-secondary-100 hover:text-secondary-500 transition-colors"
                      rel="nofollow"
                    >
                      <i className="ri-wechat-line text-lg"></i>
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-bold text-foreground-950 mb-5">
                  发送消息
                </h3>

                {contactStatus === "success" ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-accent-50 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-2xl text-accent-500"></i>
                    </div>
                    <p className="text-foreground-900 font-semibold">
                      消息发送成功！
                    </p>
                    <p className="text-sm text-foreground-500 mt-1">
                      我们会在24小时内回复您的邮件。
                    </p>
                    <button
                      onClick={() => setContactStatus("idle")}
                      className="mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer font-medium"
                    >
                      继续发送
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleContactSubmit}
                   
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) =>
                          setContactForm({ ...contactForm, name: e.target.value })
                        }
                        placeholder="请输入姓名"
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            email: e.target.value,
                          })
                        }
                        placeholder="请输入邮箱地址"
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        name="message"
                        value={contactForm.message}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            message: e.target.value,
                          })
                        }
                        placeholder="请输入您的留言..."
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all resize-none"
                      />
                      <p className="text-xs text-foreground-400 text-right mt-1">
                        {contactForm.message.length}/500
                      </p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    {contactStatus === "error" && contactError && (
                      <p className="text-xs text-red-400">{contactError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-send-plane-line"></i>
                      提交留言
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm mt-2">
                快速解答您的疑惑
              </p>
            </div>

            <div className="space-y-3">
              {faqItems.map((item, idx) => (
                <details
                  key={idx}
                  className="bg-background-100 rounded-xl overflow-hidden group cursor-pointer"
                >
                  <summary className="flex items-center justify-between px-5 py-4 text-sm font-semibold text-foreground-900 list-none select-none">
                    {item.question}
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-2"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-950">
          <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              准备好开始您的动漫之旅了吗？
            </h2>
            <p className="text-white/60 text-sm mb-8 max-w-md mx-auto">
              海量免费高清动漫资源，每日更新，无需注册即可观看
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/japanese-anime"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                开始观看动漫
              </Link>
              <Link
                to="/category/movies"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-white/30 text-white font-medium text-sm hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-movie-line text-lg"></i>
                浏览电影专区
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer formUrl={formSubmitUrl} />
    </div>
  );
}